import { valibotResolver } from "@hookform/resolvers/valibot";
import { PencilSimpleLine } from "@phosphor-icons/react";
import { useState } from "react";
import { useForm } from "react-hook-form";

import MaxLengthInput from "common/components/MaxLengthInput";
import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
} from "common/components/ui/Dialog";
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTrigger,
} from "common/components/ui/Drawer";
import FormField from "common/components/ui/FormField";
import { getSchemaMaxLength } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useCampaignFlow from "../../../../datahooks/useCampaignFlow";
import { FlowNameSchema } from "../../../../schemas";
import CampaignPausedTooltip from "../../CampaignPausedTooltip";

import type { CampaignFlow } from "../../../../types";

interface ChangeFlowNameProps {
  flow: CampaignFlow;
  isDisabled: boolean;
}

export default function ChangeFlowName({
  flow,
  isDisabled,
}: ChangeFlowNameProps) {
  const isDesktop = useTwBreakpoint("lg");
  const [isOpen, setIsOpen] = useState(false);

  const { updateFlow, isUpdatingFlow } = useCampaignFlow();

  const {
    watch,
    register,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: flow.name,
    },
    resolver: valibotResolver(FlowNameSchema),
  });

  function onSave({ name }: { name: string }) {
    updateFlow({
      flowId: flow.id,
      updates: {
        name,
      },
    }).then(() => {
      reset({ name });
      setIsOpen(false);
    });
  }

  const Component = isDesktop ? Dialog : Drawer;
  const Content = isDesktop ? DialogContent : DrawerContent;
  const Trigger = isDesktop ? DialogTrigger : DrawerTrigger;
  const Close = isDesktop ? DialogClose : DrawerClose;
  const Footer = isDesktop ? DialogFooter : DrawerFooter;
  const Body = isDesktop ? DialogBody : DrawerBody;
  const Header = isDesktop ? DialogHeader : DrawerHeader;

  return (
    <Component open={isOpen} onOpenChange={setIsOpen} onAfterClose={reset}>
      <Trigger asChild>
        <CampaignPausedTooltip isEditingDisabled={isDisabled}>
          {isDesktop ? (
            <Button
              variant="secondary-purple"
              className="ml-auto"
              disabled={isDisabled}
              leftIcon={<PencilSimpleLine />}
              onClick={() => setIsOpen(true)}
            >
              Edit name
            </Button>
          ) : (
            <Button
              intent="iconOnly"
              variant="secondary-black"
              onClick={() => setIsOpen(true)}
            >
              <PencilSimpleLine />
            </Button>
          )}
        </CampaignPausedTooltip>
      </Trigger>
      <Content>
        <Header
          title="Change Flow name"
          subtitle="You can change flow&apos;s name here"
        />

        <Body>
          <FormField
            label="Flow name"
            htmlFor="flow-name-input"
            error={errors.name?.message}
          >
            <MaxLengthInput
              variant="lg"
              id="flow-name-input"
              characterLength={watch("name").length}
              maxLength={getSchemaMaxLength(FlowNameSchema.entries.name)}
              {...register("name")}
            />
          </FormField>
        </Body>

        <Footer className="justify-end">
          <Close asChild>
            <Button variant="secondary-black" disabled={isUpdatingFlow}>
              Cancel
            </Button>
          </Close>

          <Button onClick={handleSubmit(onSave)} isLoading={isUpdatingFlow}>
            Save
          </Button>
        </Footer>
      </Content>
    </Component>
  );
}
